/** Chamadas dos indicadores e dos nascidos vivos (Módulo 8, D-047 e D-048). */
import type {
  CodigoIndicador,
  DadosNascidosVivos,
  DescricaoIndicador,
  MetasIndicadores,
  Selo,
} from '@metaepi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { enviar, requisitar } from '../../lib/api';

export interface Municipio {
  codigo_ibge: string;
  nome: string;
  uf: string;
}

export interface IndicadorCalculado {
  codigo: CodigoIndicador;
  valor: number | null;
  selo: Selo;
  rotuloSelo: string;
  motivo?: string;
  numerador: number | null;
  denominador: number | null;
  numeradorExibido: string | null;
  denominadorExibido: string | null;
  emAcompanhamento?: number;
  descricao: DescricaoIndicador;
}

export interface RespostaIndicadores {
  precisaEscolherMunicipio: boolean;
  ano: number;
  parcial: boolean;
  municipio: Municipio;
  metas: { versao: string; titulo: string; fonte: string; valores: MetasIndicadores };
  nascidosVivos: { nascidos_vivos: number; fonte: string; informado_em: string } | null;
  semResidencia: number | null;
  agregado: boolean;
  indicadores: IndicadorCalculado[];
}

export interface NascidosVivosInformados {
  municipio_ibge: string;
  municipio_nome: string;
  uf: string;
  ano: number;
  nascidos_vivos: number;
  fonte: 'digitacao' | 'planilha';
  informado_em: string;
  informado_por_nome: string | null;
}

const CHAVE = 'indicadores';

export const parametrosIndicadores = (f: { ano: number; municipio?: string }) =>
  new URLSearchParams({ ano: String(f.ano), ...(f.municipio ? { municipio: f.municipio } : {}) });

export const useMunicipiosIndicadores = () =>
  useQuery({
    queryKey: [CHAVE, 'municipios'],
    queryFn: () =>
      requisitar<{ itens: Municipio[] }>('/indicadores/municipios').then((r) => r.itens),
    staleTime: 5 * 60_000,
  });

export const useIndicadores = (f: { ano: number; municipio?: string }) =>
  useQuery({
    queryKey: [CHAVE, 'calculo', f],
    queryFn: () => requisitar<RespostaIndicadores>(`/indicadores?${parametrosIndicadores(f)}`),
  });

export const useNascidosVivos = (habilitado: boolean) =>
  useQuery({
    queryKey: [CHAVE, 'nascidos-vivos'],
    queryFn: () =>
      requisitar<{ itens: NascidosVivosInformados[] }>('/indicadores/nascidos-vivos').then(
        (r) => r.itens,
      ),
    enabled: habilitado,
  });

function useGravar<E, R = unknown>(executar: (e: E) => Promise<R>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: executar,
    onSuccess: () => cliente.invalidateQueries({ queryKey: [CHAVE] }),
  });
}

export const useInformarNascidosVivos = () =>
  useGravar((d: DadosNascidosVivos) => enviar('/indicadores/nascidos-vivos', 'PUT', d));

export const useEnviarPlanilha = () =>
  useGravar((linhas: DadosNascidosVivos[]) =>
    enviar<{ gravadas: number }>('/indicadores/nascidos-vivos/planilha', 'POST', { linhas }),
  );
